<template>
	<div :class="`${skipBaseStyles ? '' : 'rich-text'} ${blok.textAlign || ''}`">
		<StoryblokRichText :doc="blok.content" :resolvers="resolvers" />
	</div>
</template>

<script setup lang="ts">
import type { BlokRichText } from "~/types/storyblok/storyblok";
import type { StoryblokRichTextNode } from "@storyblok/richtext";

interface Props {
	blok: BlokRichText;
	skipBaseStyles?: boolean;
}
const { blok, skipBaseStyles } = defineProps<Props>();

const resolvers = {
	bullet_list: ({ children }: StoryblokRichTextNode) =>
		h(
			"ul",
			{
				class: "list-disc pl-8"
			},
			children
		),
	ordered_list: ({ children }: StoryblokRichTextNode) =>
		h(
			"ol",
			{
				class: "list-decimal pl-8"
			},
			children
		),
	table: ({ children }: StoryblokRichTextNode) =>
		h("div", { class: "overflow-x-auto whitespace-nowrap" }, [
			h(
				"table",
				{
					class: `table-auto w-full border-collapse min-w-full ${skipBaseStyles ? "" : "border border-gray-300"}`
				},
				[h("tbody", children)]
			)
		]),
	paragraph: ({ children }: StoryblokRichTextNode) => {
		const childrenTypes = Array.isArray(children)
			? children.map((child) => child.type)
			: [];
		const imgInText =
			childrenTypes.indexOf("img") === 0 && childrenTypes.length > 1;
		let imgChildren;

		if (imgInText) {
			imgChildren = h("div", { class: "flex items-center gap-4" }, [
				children?.[0],
				h("div", children?.slice(1))
			]);
		}

		return !imgInText ? h("p", {}, children) : h("div", {}, imgChildren);
	},
	tableCell: ({ children }: StoryblokRichTextNode) =>
		h(
			"td",
			{ class: `px-4 py-2 ${skipBaseStyles ? "" : "border border-gray-300"}` },
			children
		),
	tableHeader: ({ children }: StoryblokRichTextNode) =>
		h(
			"th",
			{
				class: `px-4 py-2 ${skipBaseStyles ? "" : "bg-gray-100 font-semibold text-gray-800 border border-gray-300"}`
			},
			children
		),
	tableRow: ({ children }: StoryblokRichTextNode) =>
		h("tr", { class: skipBaseStyles ? "" : "odd:bg-gray-100" }, children)
};
</script>
